import React from 'react';

/**
 * Button Component
 *
 * A delightful, accessible button with multiple variants and states.
 * Designed for touch-first with 44px minimum touch targets.
 */

const variants = {
  primary: 'bg-gradient-to-r from-coral-500 to-coral-600 text-white hover:from-coral-600 hover:to-coral-700 shadow-md hover:shadow-lg focus:ring-2 focus:ring-coral-500 focus:ring-offset-2',
  secondary: 'bg-slate-100 text-slate-800 hover:bg-slate-200 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 focus:ring-2 focus:ring-slate-400',
  gold: 'bg-gradient-to-r from-gold-500 to-gold-600 text-white hover:from-gold-600 hover:to-gold-700 shadow-md hover:shadow-lg focus:ring-2 focus:ring-gold-500 focus:ring-offset-2',
};

const sizes = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-5 py-3 text-base',
  lg: 'px-6 py-4 text-lg',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  className = '',
  onClick,
  type = 'button',
  ...props
}) {
  const baseClasses = 'btn';
  const variantClasses = variants[variant] || variants.primary;
  const sizeClasses = sizes[size] || sizes.md;
  const widthClasses = fullWidth ? 'w-full' : '';
  const loadingClasses = loading ? 'opacity-70 cursor-wait' : '';

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`
        ${baseClasses}
        ${variantClasses}
        ${sizeClasses}
        ${widthClasses}
        ${loadingClasses}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}

      {!loading && icon && iconPosition === 'left' && (
        <span className="flex-shrink-0">{icon}</span>
      )}

      <span>{children}</span>

      {!loading && icon && iconPosition === 'right' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </button>
  );
}

/**
 * IconButton - For icon-only buttons (FAB, close, etc.)
 */
export function IconButton({
  children,
  variant = 'ghost',
  size = 'md',
  className = '',
  label,
  ...props
}) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-11 h-11',
    lg: 'w-14 h-14',
  };

  return (
    <button
      className={`
        btn ${variants[variant]}
        ${sizeClasses[size]}
        p-0 rounded-full
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  );
}

export default Button;
